import {
  proStudioUrqlGql as urqlGql,
  RuleCreationTypeEnum,
} from '@pypestream/api-services';

import { checkForErrors } from './check-for-errors';

/**
 * A single masking rule within a rule set. A rule holds ONE regex value
 * (PSTUDIO-3804 removed the "rule pattern" sub-layer), but the backend field is
 * still a list, so `patterns` stays an array here: rules created before the
 * change may carry more than one, and dropping them silently would change what
 * a shipped rule masks.
 */
export interface MaskingRule {
  id: string;
  name: string;
  description: string | null;
  /** SYSTEM rules are predefined and read-only; MANUALLY_CREATED are editable. */
  type: RuleCreationTypeEnum;
  patterns: string[];
}

/**
 * Display names for the predefined SYSTEM rules, keyed by the name they were
 * seeded with.
 *
 * PSTUDIO-3804 renamed the Credit Card default so it states which brands it
 * covers, the way "Social Security Number (US)" already does. The name is
 * persisted per rule set at creation time, so changing only the seed constant
 * would leave every existing project on the old name forever — SYSTEM rules
 * aren't user-editable, so nobody could fix it by hand. Overriding on read
 * makes the rename apply everywhere; it is safe precisely because these rules
 * are read-only (no edit form can write the display name back).
 */
const SYSTEM_RULE_DISPLAY_NAMES: Record<string, string> = {
  'Credit Card':
    'Credit Card (Visa, Mastercard, Amex, Discover, Diners Club, JCB)',
};

/** A pype (per env) this rule set is applied to. `env` is 'Live' | 'Sandbox'. */
export interface RuleSetPype {
  pypeId: string;
  env: string;
}

/** A "rule set" == a `PypeRuleGroup` (project-scoped). */
export interface RuleSet {
  id: string;
  name: string;
  description: string | null;
  type: RuleCreationTypeEnum;
  /** Channels/pypes (per env) this rule set is assigned to. */
  pypes: RuleSetPype[];
  rules: MaskingRule[];
}

/**
 * The project's default rule set — the one a channel falls back to when no rule
 * set is explicitly assigned to it. It is created by the backend as a SYSTEM
 * rule group (user-created sets are always MANUALLY_CREATED), holds the Credit
 * Card + SSN masks, and cannot be deleted (PSTUDIO-3804).
 */
export const isDefaultRuleSet = (ruleSet: RuleSet) =>
  ruleSet.type === RuleCreationTypeEnum.System;

/**
 * Loads every masking rule set for a project. The operations live on the
 * `pypestream-admin-backend` remote schema, which Hasura namespaces under
 * `admin_`, so the payload is unwrapped here and normalized into the flat
 * `RuleSet` shape the rest of the app consumes.
 */
export const getPypeRuleGroups = async ({
  projectId,
}: {
  projectId: string;
}): Promise<RuleSet[]> => {
  const response = await urqlGql.getPypeRuleGroups({ projectId });

  checkForErrors(response);

  const groups = response.data?.admin_?.pypeRuleGroupsByProjectId ?? [];

  return groups.filter(Boolean).map((group) => ({
    id: group?.id ?? '',
    name: group?.name ?? '',
    description: group?.description ?? null,
    type: group?.type ?? RuleCreationTypeEnum.ManuallyCreated,
    pypes: (group?.pypes ?? [])
      .filter(Boolean)
      .filter((pype) => pype?.pypeId && pype?.env)
      .map((pype) => ({
        pypeId: pype?.pypeId ?? '',
        env: (pype?.env as string) ?? '',
      })),
    rules: (group?.rules ?? []).filter(Boolean).map((rule) => ({
      id: rule?.id ?? '',
      name:
        (rule?.type === RuleCreationTypeEnum.System &&
          SYSTEM_RULE_DISPLAY_NAMES[rule?.name ?? '']) ||
        rule?.name ||
        '',
      description: rule?.description ?? null,
      type: rule?.type ?? RuleCreationTypeEnum.ManuallyCreated,
      patterns: (rule?.rules ?? []).filter(
        (pattern): pattern is string => typeof pattern === 'string'
      ),
    })),
  }));
};
